import { useEffect } from "react";
import { FiUploadCloud, FiX } from "react-icons/fi";
import DateTime from "../../../../components/datetime";
import {
  FileUploadPayload,
  useFileUpload,
} from "../../../../components/files/useFileUpload";
import { doNothing } from "../../../../utils/functional";
import { ValidationMessage } from "@/flokit/core/validation-message/validation-message";
import { showToast } from "@/flokit/core/toast/show-toast";

interface EmptyDocumentProps {
  onUpload: (payload: FileUploadPayload) => void;
  onDelete: () => void;
  docName: string;
  uploadedDate?: string;
  required?: boolean;
  shouldShowValidation?: boolean;
  validationMessage?: string;
}

export const UploadDocumentRow = (props: EmptyDocumentProps) => {
  const { payload, askUserForFile, resetFileState, errorState } = useFileUpload(
    {
      onChange: doNothing,
      maxFileSize: 5000000,
      fileTypes: ["application/pdf"],
      errorMessages: {
        fileSize: "File must be less than 5MB",
      },
    },
  );

  useEffect(() => {
    if (errorState?.fileSize.isInvalid) {
      showToast({ type: "error", message: errorState.fileSize.message });
      resetFileState();
    }
    if (errorState?.fileType.isInvalid) {
      showToast({ type: "error", message: errorState.fileType.message });
      resetFileState();
    }
  }, [errorState.fileSize, errorState.fileType, resetFileState]);

  useEffect(() => {
    if (payload.file) {
      if (payload.fileSize > 5000000) {
        resetFileState();
        showToast({
          type: "error",
          message: "File exceeds 5mb",
          autoClose: 10000,
        });
      }
      props.onUpload({ ...payload });
    }
  }, [payload]);

  return payload.file === null && !props.uploadedDate ? (
    <div className="flex flex-col gap-y-1">
      <button
        onClick={() => askUserForFile()}
        className="flex flex-col rounded-md border border-dashed border-dublin-gray bg-white px-4 py-2 text-left text-base text-navy"
      >
        <span className="flex items-center gap-x-2">
          <FiUploadCloud className="flex-shrink-0" />
          <span className="font-medium">Upload {props.docName}</span>
          {props?.required ? (
            <span className="text-ui-red">
              *<span className="sr-only">Required</span>
            </span>
          ) : null}
        </span>
        <ValidationMessage
          shouldShowValidation={props.shouldShowValidation}
          validationMessage={props.validationMessage}
        />
      </button>
    </div>
  ) : (
    <div className="flex items-center gap-x-2 rounded-md bg-friendly-eskimo px-4 py-2 text-base text-navy">
      <FiUploadCloud className="flex-shrink-0" />
      <span className="line-clamp-2 inline-flex flex-1 font-medium">
        {props.docName}: {payload.fileName ? payload.fileName : "Uploaded on "}
        {props.uploadedDate ? (
          <DateTime
            date={new Date(props.uploadedDate)}
            format={"MMMM dd yyyy"}
          />
        ) : null}
        {props?.required ? (
          <span className="ml-2 text-ui-red">
            *<span className="sr-only">Required</span>
          </span>
        ) : null}
      </span>
      <button
        onClick={() => {
          props.onDelete();
          resetFileState();
        }}
        className="text-charcoal hover:text-ui-red"
      >
        <FiX />
      </button>
    </div>
  );
};
